/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNodesState, useEdgesState } from "reactflow";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useTheme } from "@mui/material/styles";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { PtwSettingsData } from "./workFlow-context";
import { Tooltip, IconButton } from "@mui/material";
import { Stack, Chip, Avatar, Typography, Box } from "@mui/material";
import RootIcon from "@mui/icons-material/Home";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTranslation } from "react-i18next";

const StyledNode = styled("div")(({ theme, isRoot, isDarkMode }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: isRoot
    ? theme.palette.primary.main
    : isDarkMode
      ? theme.palette.grey[900]
      : theme.palette.grey[100],
  color: theme.palette.getContrastText(
    isRoot
      ? theme.palette.primary.main
      : isDarkMode
        ? theme.palette.grey[900]
        : theme.palette.grey[100]
  ),
  borderRadius: "16px",
  border: `2px solid ${
    isRoot
      ? theme.palette.primary.dark
      : isDarkMode
        ? theme.palette.grey[700]
        : theme.palette.grey[300]
  }`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: theme.shadows[4],
  position: "relative",
  padding: theme.spacing(2),
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[8],
  },
}));

const useDuplicateWorkflowData = () => {
  const { t } = useTranslation();
  const {
    selectedWorkflow,
    userGroupsList,
    inputTypeList,
    goBackToList,
    refetchWorkflows,
    workflowAction,
  } = PtwSettingsData();
  const theme = useTheme();
  const {
    mutate: postData,
    isPending: postIsLoading,
    error: postError,
  } = usePostJsonData();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const { setItem } = useLocalStorage("duplicateWorkflow");

  const [confirmDeleteNodeId, setConfirmDeleteNodeId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(theme.palette.mode === "dark");
  const [workflowData, setWorkflowData] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [modalButton, setModalButton] = useState("");
  const [userGroupIds, setUserGroupIds] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [nodeData, setNodeData] = useState({
    action_name: "",
    status: "",
    self_actionable: false,
    userGroupIds: [],
    step_inputs: [],
  });

  // Memoize dependencies with stable references
  const stableSelectedWorkflow = useMemo(
    () => selectedWorkflow,
    [selectedWorkflow?.id]
  );
  const stableInputTypeList = useMemo(
    () => inputTypeList,
    [JSON.stringify(inputTypeList)]
  );
  const stableUserGroupsList = useMemo(
    () => userGroupsList,
    [JSON.stringify(userGroupsList)]
  );

  // Modal handling
  const openModal = useCallback((nodeId) => {
    setModalButton("create");
    setSelectedNode(nodeId);
    setNodeData({
      action_name: "",
      status: "",
      self_actionable: false,
      userGroupIds: [],
      step_inputs: [],
    });
    setUserGroupIds([]);
    setIsModalOpen(true);
  }, []);

  const openUpdateModal = useCallback(
    (nodeId) => {
      setModalButton("update");
      const step = workflowData?.steps.find((s) => s.id === nodeId);
      if (step) {
        setSelectedNode(nodeId);
        setNodeData({
          action_name: step.action_name,
          status: step.status,
          self_actionable: step.self_actionable,
          userGroupIds: step.userGroupIds,
          step_inputs: Array.isArray(step.step_inputs)
            ? step.step_inputs.map((input) => ({
                ...input,
                type:
                  stableInputTypeList.find(
                    (option) => option.value === input.input_type_id
                  )?.input_key || "",
                options: input.options || [],
              }))
            : [],
        });
        setUserGroupIds(
          step.userGroupIds.map((id) => ({
            value: id,
            label:
              stableUserGroupsList.find((group) => group.value === id)?.label ||
              t("duplicateWorkflowData.unknownLabel"),
          }))
        );
        setIsModalOpen(true);
      }
    },
    [workflowData, stableUserGroupsList, stableInputTypeList, t]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedNode(null);
    setNodeData({
      action_name: "",
      status: "",
      self_actionable: false,
      userGroupIds: [],
      step_inputs: [],
    });
    setUserGroupIds([]);
  }, []);

  // Node manipulation
  const deleteNode = useCallback((nodeId) => {
    setConfirmDeleteNodeId(nodeId);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeleteNode = useCallback(() => {
    if (!confirmDeleteNodeId || !workflowData) return;

    const nodeId = confirmDeleteNodeId;
    const deletedStep = workflowData.steps.find((step) => step.id === nodeId);
    if (!deletedStep) return;

    const parentId = deletedStep.parent_id || workflowData.id;
    const children = workflowData.steps.filter(
      (step) => step.parent_id === nodeId
    );

    setWorkflowData((prev) => ({
      ...prev,
      steps: prev.steps
        .map((step) =>
          children.some((child) => child.id === step.id)
            ? { ...step, parent_id: parentId }
            : step
        )
        .filter((step) => step.id !== nodeId),
    }));

    setNodes((prev) => prev.filter((node) => node.id !== nodeId));
    setEdges((prev) => {
      const filteredEdges = prev.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      );
      const newEdges = children.map((child) => ({
        id: `edge-${parentId}-${child.id}`,
        source: parentId,
        target: child.id,
        label: child.action_name || t("duplicateWorkflowData.unknownLabel"),
      }));
      return [...filteredEdges, ...newEdges];
    });

    setToast({
      open: true,
      severity: "info",
      message: t("duplicateWorkflowData.stepDeletedToast", {
        count: children.length,
      }),
    });

    setConfirmDeleteNodeId(null);
    setDeleteDialogOpen(false);
  }, [confirmDeleteNodeId, workflowData, setNodes, setEdges, setToast, t]);

  const addChildNode = useCallback(() => {
    if (!selectedNode) return;

    const newChildId = uuidv4();
    const parentNode = nodes.find((node) => node.id === selectedNode);
    if (!parentNode) return;

    const parentLevel = Math.floor(parentNode.position.y / 180);
    const childLevel = parentLevel + 1;
    const childY = 100 + childLevel * 180;
    const siblingNodes = nodes.filter(
      (node) => Math.abs(node.position.y - childY) < 1
    );
    const currentX = siblingNodes.length * 240;

    const newNode = {
      id: newChildId,
      position: { x: currentX, y: childY },
      style: {
        background: "transparent",
        border: "none",
        width: 240,
        height: 120,
        zIndex: 1,
      },
      data: {
        label: (
          <StyledNode isDarkMode={isDarkMode}>
            <IconButton
              onClick={() => deleteNode(newChildId)}
              size="small"
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                bgcolor: theme.palette.error.main,
                color: "#fff",
                "&:hover": { bgcolor: theme.palette.error.dark },
                zIndex: 2,
              }}
              aria-label={t("duplicateWorkflowData.deleteNodeAria", {
                status:
                  nodeData.status || t("duplicateWorkflowData.newStepLabel"),
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              onClick={() => openUpdateModal(newChildId)}
              sx={{
                height: "100%",
                width: "100%",
                px: 1.5,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? theme.palette.grey[900]
                    : theme.palette.grey[100],
                },
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" align="center">
                {t("duplicateWorkflowData.statusLabel", {
                  status:
                    nodeData.status || t("duplicateWorkflowData.newStepLabel"),
                })}
              </Typography>
              {nodeData.self_actionable && (
                <Chip
                  label={t("duplicateWorkflowData.selfActionableLabel")}
                  color="success"
                  size="small"
                  icon={<CheckCircleIcon />}
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
            <Box
              sx={{
                position: "absolute",
                bottom: 4,
                right: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Tooltip title={t("duplicateWorkflowData.addStepTooltip")} arrow>
                <IconButton
                  onClick={() => openModal(newChildId)}
                  size="small"
                  sx={{
                    bgcolor: theme.palette.success.main,
                    color: "#fff",
                    "&:hover": { bgcolor: theme.palette.success.dark },
                  }}
                  aria-label={t("duplicateWorkflowData.addStepAria", {
                    status:
                      nodeData.status ||
                      t("duplicateWorkflowData.newStepLabel"),
                  })}
                >
                  <AddCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </StyledNode>
        ),
      },
    };

    const newEdge = {
      id: `edge-${selectedNode}-${newChildId}`,
      source: selectedNode,
      target: newChildId,
      label: nodeData.action_name || t("duplicateWorkflowData.unknownLabel"),
    };

    setWorkflowData((prev) => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          id: newChildId,
          action_name: nodeData.action_name,
          parent_id: selectedNode,
          status: nodeData.status,
          self_actionable: nodeData.self_actionable,
          userGroupIds: userGroupIds.map((group) => group.value),
          step_inputs: nodeData.step_inputs.map((input) => ({
            ...input,
            type:
              stableInputTypeList.find(
                (option) => option.value === input.input_type_id
              )?.input_key || "",
          })),
        },
      ],
    }));

    setNodes((prev) => [...prev, newNode]);
    setEdges((prev) => [...prev, newEdge]);
    setItem(workflowData);
    setToast({
      open: true,
      severity: "success",
      message: t("duplicateWorkflowData.stepCreatedToast"),
    });
    closeModal();
  }, [
    selectedNode,
    nodes,
    nodeData,
    userGroupIds,
    workflowData,
    setNodes,
    setEdges,
    setItem,
    setToast,
    closeModal,
    isDarkMode,
    theme,
    deleteNode,
    openModal,
    openUpdateModal,
    stableInputTypeList,
    t,
  ]);

  const updateNodeData = useCallback(() => {
    if (!selectedNode || !workflowData) return;

    setWorkflowData((prev) => {
      const updated = {
        ...prev,
        steps: prev.steps.map((step) =>
          step.id === selectedNode
            ? {
                ...step,
                action_name: nodeData.action_name,
                status: nodeData.status,
                self_actionable: nodeData.self_actionable,
                userGroupIds: userGroupIds.map((group) => group.value),
                step_inputs: nodeData.step_inputs.map((input) => ({
                  ...input,
                  type:
                    stableInputTypeList.find(
                      (option) => option.value === input.input_type_id
                    )?.input_key || "",
                })),
              }
            : step
        ),
      };
      setItem(updated);
      return updated;
    });

    setToast({
      open: true,
      severity: "success",
      message: t("duplicateWorkflowData.stepUpdatedToast"),
    });
    closeModal();
  }, [
    selectedNode,
    nodeData,
    userGroupIds,
    workflowData,
    setItem,
    setToast,
    closeModal,
    stableInputTypeList,
    t,
  ]);

  const moveStepInputUp = useCallback((index) => {
    if (index === 0) return;
    setNodeData((prev) => {
      const steps = [...prev.step_inputs];
      [steps[index - 1], steps[index]] = [steps[index], steps[index - 1]];
      const updatedSteps = steps.map((step, idx) => ({
        ...step,
        position: idx,
      }));
      return { ...prev, step_inputs: updatedSteps };
    });
  }, []);

  const moveStepInputDown = useCallback(
    (index) => {
      if (index >= nodeData.step_inputs.length - 1) return;
      setNodeData((prev) => {
        const steps = [...prev.step_inputs];
        [steps[index], steps[index + 1]] = [steps[index + 1], steps[index]];
        const updatedSteps = steps.map((step, idx) => ({
          ...step,
          position: idx,
        }));
        return { ...prev, step_inputs: updatedSteps };
      });
    },
    [nodeData.step_inputs.length]
  );

  const changeHandlerName = useCallback(
    (e) => {
      const newName = e.target.value;
      setWorkflowData((prev) => {
        const updated = { ...prev, name: newName };
        setItem(updated);
        return updated;
      });
    },
    [setItem]
  );

  const handleMultiChange = useCallback(
    (field) => (selectedOptions) => {
      if (field === "user_groups") {
        setUserGroupIds(selectedOptions);
      }
    },
    []
  );

  const changeTarget = useCallback((newTarget) => {
    setWorkflowData((prev) => ({
      ...prev,
      target: newTarget,
    }));
  }, []);

  const handleInputTypeChange = useCallback(
    (index, field, value) => {
      setNodeData((prev) => {
        const newStepInputs = [...(prev.step_inputs || [])];
        if (!newStepInputs[index]) {
          newStepInputs[index] = {
            input_type_id: "",
            type: "",
            name: "",
            description: "",
            required: false,
            default_value: "",
            position: 0,
            options: [],
          };
        }

        if (field === "input_type_id") {
          const newValue = typeof value === "object" ? value.value : value;
          const selectedInputType = stableInputTypeList.find(
            (option) => option.value === newValue
          );
          newStepInputs[index] = {
            ...newStepInputs[index],
            input_type_id: newValue,
            type: selectedInputType?.input_key || "",
            options:
              selectedInputType?.input_key === "selection" ||
              selectedInputType?.input_key === "multi_selection"
                ? newStepInputs[index].options || []
                : [],
          };
        } else if (field === "position") {
          const newValue = parseInt(value, 10);
          newStepInputs[index] = {
            ...newStepInputs[index],
            position: isNaN(newValue) || newValue < 0 ? 0 : newValue,
          };
        } else {
          newStepInputs[index] = {
            ...newStepInputs[index],
            [field]: value,
          };
        }

        return { ...prev, step_inputs: newStepInputs };
      });
    },
    [stableInputTypeList]
  );

  const addNewStepInput = useCallback(() => {
    setNodeData((prev) => {
      const existingSteps = Array.isArray(prev.step_inputs)
        ? prev.step_inputs
        : [];
      const nextPosition =
        existingSteps.length > 0
          ? Math.max(...existingSteps.map((step) => step.position)) + 1
          : 0;

      return {
        ...prev,
        step_inputs: [
          ...existingSteps,
          {
            input_type_id: "",
            type: "",
            name: "",
            description: "",
            required: false,
            default_value: "",
            position: nextPosition,
            options: [],
          },
        ],
      };
    });
  }, []);

  const removeStepInput = useCallback((index) => {
    setNodeData((prev) => {
      const newStepInputs = [...(prev.step_inputs || [])];
      newStepInputs.splice(index, 1);
      return { ...prev, step_inputs: newStepInputs };
    });
  }, []);

  const addOption = useCallback((index, option) => {
    if (!option.trim()) return;
    setNodeData((prev) => {
      const newStepInputs = [...(prev.step_inputs || [])];
      newStepInputs[index] = {
        ...newStepInputs[index],
        options: [...(newStepInputs[index].options || []), option.trim()],
      };
      return { ...prev, step_inputs: newStepInputs };
    });
  }, []);

  const removeOption = useCallback((index, optionIndex) => {
    setNodeData((prev) => {
      const newStepInputs = [...(prev.step_inputs || [])];
      newStepInputs[index] = {
        ...newStepInputs[index],
        options: newStepInputs[index].options.filter(
          (_, i) => i !== optionIndex
        ),
      };
      return { ...prev, step_inputs: newStepInputs };
    });
  }, []);

  // Initialize workflowData
  useEffect(() => {
    if (
      !stableSelectedWorkflow ||
      workflowData ||
      !stableInputTypeList?.length ||
      workflowAction !== "duplicate"
    )
      return;

    const stepIdMap = new Map();
    const newSteps = stableSelectedWorkflow.steps.map((step) => {
      const newId = uuidv4();
      stepIdMap.set(step.id, newId);
      return {
        ...step,
        id: newId,
        userGroupIds: step.user_groups
          ? step.user_groups.map((group) => group.id)
          : [],
        step_inputs: Array.isArray(step.step_inputs)
          ? step.step_inputs.map((input) => ({
              ...input,
              type:
                stableInputTypeList.find(
                  (option) => option.value === input.input_type_id
                )?.input_key || "",
              options: input.options || [],
            }))
          : [],
      };
    });

    const updatedSteps = newSteps.map((step) => ({
      ...step,
      parent_id: step.parent_id ? stepIdMap.get(step.parent_id) || null : null,
    }));

    const newWorkflow = {
      ...stableSelectedWorkflow,
      id: uuidv4(),
      name: "",
      target: "",
      steps: updatedSteps,
    };

    setWorkflowData(newWorkflow);
    setItem(newWorkflow);
  }, [stableSelectedWorkflow, stableInputTypeList, workflowAction, setItem]);

  // Update dark mode
  useEffect(() => {
    setIsDarkMode(theme.palette.mode === "dark");
  }, [theme.palette.mode]);

  // Render nodes and edges
  useEffect(() => {
    if (!workflowData || workflowAction !== "duplicate") {
      setNodes([]);
      setEdges([]);
      return;
    }

    const nodes = [];
    const edges = [];
    const parentChildMap = new Map();

    // Root node
    nodes.push({
      id: workflowData.id,
      position: { x: 180, y: 0 },
      style: {
        background: "transparent",
        border: "none",
        width: 240,
        height: 120,
        zIndex: 1,
      },
      data: {
        label: (
          <StyledNode isRoot isDarkMode={isDarkMode}>
            <Box onClick={() => setDrawerOpen(true)}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
              >
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.dark,
                    width: 28,
                    height: 28,
                  }}
                >
                  <RootIcon fontSize="small" />
                </Avatar>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  textAlign="center"
                >
                  {workflowData.name ||
                    t("duplicateWorkflowData.newWorkflowLabel")}
                </Typography>
              </Stack>
            </Box>
          </StyledNode>
        ),
      },
    });

    // Build parent-child map
    workflowData.steps.forEach((step) => {
      const parentId = step.parent_id || workflowData.id;
      if (!parentChildMap.has(parentId)) parentChildMap.set(parentId, []);
      parentChildMap.get(parentId).push(step);
    });

    // Process levels and assign positions
    let currentY = 100;
    const processLevel = (parentId, level = 0) => {
      if (!parentChildMap.has(parentId)) return;
      let currentX = 0;

      parentChildMap.get(parentId).forEach((step) => {
        const nodeId = step.id;
        const x = currentX;
        const y = currentY + level * 180;

        nodes.push({
          id: nodeId,
          position: { x, y },
          style: {
            background: "transparent",
            border: "none",
            width: 240,
            height: 120,
            zIndex: 1,
          },
          data: {
            label: (
              <StyledNode isDarkMode={isDarkMode}>
                {step.parent_id && (
                  <IconButton
                    onClick={() => deleteNode(nodeId)}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: theme.palette.error.main,
                      color: "#fff",
                      "&:hover": { bgcolor: theme.palette.error.dark },
                      zIndex: 2,
                    }}
                    aria-label={t("duplicateWorkflowData.deleteNodeAria", {
                      status:
                        step.status || t("duplicateWorkflowData.newStepLabel"),
                    })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <Box
                  onClick={() => openUpdateModal(nodeId)}
                  sx={{
                    height: "100%",
                    width: "100%",
                    px: 1.5,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? theme.palette.grey[900]
                        : theme.palette.grey[100],
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    align="center"
                  >
                    {t("duplicateWorkflowData.statusLabel", {
                      status:
                        step.status || t("duplicateWorkflowData.newStepLabel"),
                    })}
                  </Typography>
                  {step.self_actionable && (
                    <Chip
                      label={t("duplicateWorkflowData.selfActionableLabel")}
                      color="success"
                      size="small"
                      icon={<CheckCircleIcon />}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Tooltip
                    title={t("duplicateWorkflowData.addStepTooltip")}
                    arrow
                  >
                    <IconButton
                      onClick={() => openModal(nodeId)}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.success.main,
                        color: "#fff",
                        "&:hover": { bgcolor: theme.palette.success.dark },
                      }}
                      aria-label={t("duplicateWorkflowData.addStepAria", {
                        status:
                          step.status ||
                          t("duplicateWorkflowData.newStepLabel"),
                      })}
                    >
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledNode>
            ),
          },
        });
        currentX += 240;
        processLevel(nodeId, level + 1);
      });
    };

    processLevel(workflowData.id);
    parentChildMap.forEach((childSteps, parentId) => {
      childSteps.forEach((step) => {
        edges.push({
          id: `edge-${parentId}-${step.id}`,
          source: parentId,
          target: step.id,
          label: step.action_name || t("duplicateWorkflowData.unknownLabel"),
        });
      });
    });

    setNodes(nodes);
    setEdges(edges);
  }, [
    workflowData,
    isDarkMode,
    setNodes,
    setEdges,
    theme,
    deleteNode,
    openModal,
    openUpdateModal,
    workflowAction,
    t,
  ]);

  // Helper function to process default_value based on input type
  const processDefaultValue = useCallback((defaultValue, inputTypeKey) => {
    // If no default value provided, return null
    if (!defaultValue || defaultValue === "") {
      return null;
    }

    const key = inputTypeKey?.toLowerCase();

    switch (key) {
      case "short_text":
      case "text":
        // Text types: keep as string
        return defaultValue;

      case "numeric": {
        // Numeric: convert to number, return null if not a valid number
        const numValue = Number(defaultValue);
        return isNaN(numValue) ? null : numValue;
      }

      case "boolean": {
        // Boolean: convert to actual boolean
        if (typeof defaultValue === "boolean") {
          return defaultValue;
        }
        const strValue = String(defaultValue).toLowerCase();
        return strValue === "true" || strValue === "yes" || strValue === "1";
      }

      case "confirmation":
      case "file_upload":
      case "timestamp":
      case "selection":
      case "multi_selection":
      case "user_selection":
      case "multi_user_selection":
        // These types: always null (no default values)
        return null;

      default:
        return null;
    }
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!workflowData) return;

      const sentData = {
        name: workflowData.name,
        target: workflowData.target,
        steps: workflowData.steps.map((step) => ({
          id: step.id,
          parent_id: step.parent_id,
          action_name: step.action_name,
          status: step.status,
          self_actionable: step.self_actionable,
          user_group_ids: step.userGroupIds,
          step_inputs: step.step_inputs.map((input) => {
            const inputType = stableInputTypeList.find(
              (option) => option.value === input.input_type_id
            );
            const processedDefaultValue = processDefaultValue(
              input.default_value,
              inputType?.input_key
            );

            return {
              input_type_id: input.input_type_id,
              type: inputType?.input_key || "",
              name: input.name,
              description: input.description || "",
              position: input.position,
              required: input.required || false,
              default_value: processedDefaultValue,
              ...(input.options?.length > 0 ? { options: input.options } : {}),
            };
          }),
        })),
      };

      const url = "/api/v1/org-admin/workflows";
      try {
        await postData(
          { url, data: sentData },
          {
            onSuccess: () => {
              setWorkflowData(null);
              setNodes([]);
              setEdges([]);
              setToast({
                open: true,
                severity: "success",
                message: t("duplicateWorkflowData.workflowCreatedToast"),
              });
              refetchWorkflows();
              goBackToList();
            },
            onError: (err) => {
              handleApiError(err);
            },
          }
        );
      } catch (err) {
        handleApiError(err);
      }
    },
    [
      workflowData,
      postData,
      setNodes,
      setEdges,
      setToast,
      handleApiError,
      refetchWorkflows,
      goBackToList,
      stableInputTypeList,
      processDefaultValue,
      t,
    ]
  );

  return {
    workflowData,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    isModalOpen,
    closeModal,
    nodeData,
    setNodeData,
    userGroupIds,
    modalButton,
    addChildNode,
    updateNodeData,
    deleteNode,
    changeHandlerName,
    handleSubmit,
    handleMultiChange,
    handleInputTypeChange,
    addNewStepInput,
    removeStepInput,
    addOption,
    removeOption,
    toast,
    handleToastClose,
    userGroupsList: stableUserGroupsList,
    inputTypeList: stableInputTypeList,
    changeTarget,
    moveStepInputDown,
    moveStepInputUp,
    confirmDeleteNode,
    deleteDialogOpen,
    setDeleteDialogOpen,
    drawerOpen,
    setDrawerOpen,
  };
};

export default useDuplicateWorkflowData;
